/**
 * Platform Updates — the release-notes feed rendered by PlatformUpdatesPage.
 *
 * GRANULAR, day-by-day: one entry per day that shipped (split by area when a
 * day spanned both CRM and Support), newest first, with every user-facing
 * change from that day as its own bullet. Curated from the full deploy
 * history (Apr 2026 → today). Pure repo chores (runlogs, gitignore) excluded.
 *
 * To add a screenshot to an entry: drop a PNG in
 * packages/dashboard/public/updates/ and set `image: '/updates/<file>.png'`
 * on the entry — no other change needed.
 */

export interface UpdateEntry {
  /** ISO date the update shipped. */
  date: string
  title: string
  /** Area tags rendered as chips. */
  tags: Array<'CRM' | 'Support' | 'Widget' | 'Mobile' | 'Platform-wide' | 'AI'>
  bullets: string[]
  /** Optional screenshot path under /public (e.g. '/updates/foo.png'). */
  image?: string
}

export const UPDATES: UpdateEntry[] = [
  // ─── August 2026 ─────────────────────────────────────────────────────────
  {
    date: '2026-08-03',
    title: 'Calendar visibility & mobile sync',
    tags: ['CRM', 'Mobile'],
    bullets: [
      'All Calendly demos are visible to everyone, and demos appear in the booking day panel.',
      "Colleague demos render as amber 'info only' cards per viewer; the host sees them in normal purple.",
      'Calendar palette rev 2: your items blue, colleague items grey info-only, plain Google events light grey.',
      "Demo-attendee groups: a demo counts as 'mine' (blue) for the host AND the attending team.",
      'Server-side segments + global call-queue counts.',
      'Mobile Update 02: the leads list includes cancelled/churned leads (includeTerminal).',
    ],
  },
  // ─── July 2026 ───────────────────────────────────────────────────────────
  {
    date: '2026-07-31',
    title: 'Calls dock, Call Queue & Google two-way booking',
    tags: ['CRM', 'Support'],
    bullets: [
      'Calls dock: a right-edge day-diary tab (support mode) opening a vertical day-calendar panel; the tab is draggable.',
      'Dock detail: full detail card with an Open Lead shortcut; overlapping events render as equal side-by-side columns; a centred detail modal replaces the footer strip.',
      'Call Queue (Phases 1+2): auto-enqueue triggers + a shared-pool UI so the team works one list.',
      'Google Calendar write: CRM meetings create real Google events with Meet links.',
      'Google → CRM booking sync: cancellations and reschedules in Google flow back into the CRM.',
      'Schedule feed correlates Google events to CRM leads (Phase 1) and de-dupes by identity (googleEventId/meetUrl), never by start time.',
      'Booking confirmation screen, warm invite copy, and wider lead matching.',
      'Booking edit mode + cancelled-meeting follow-up actions.',
      'Automatic cancellation follow-ups: a lead task set as NEXT plus a personal to-do entry.',
      'Attendee type-ahead: lead contacts + WeTask staff suggestions.',
      'Meetings tab: instant Google reconcile on open (per-deal scope).',
    ],
  },
  {
    date: '2026-07-30',
    title: 'Board card layouts & health honesty',
    tags: ['CRM'],
    bullets: [
      'Dedicated card layout for the Cancelled/Churned and Archived columns.',
      'Health scoring honesty: a single zero-usage penalty + a truthful "Last Usage" figure on cards.',
      'Unified schedule feed: bookings + Google Calendar + Calendly demos in one response.',
    ],
  },
  {
    date: '2026-07-29',
    title: 'The three-role model',
    tags: ['Platform-wide'],
    bullets: [
      'Roles collapse to three: Claude Dev (everything), Dev (dev mode + support, no CRM), Sales & Support (support + CRM).',
      'The Settings gear is visible to everyone; the full Settings module opens to every logged-in user.',
      'All three roles get full access (settings, CRM, dashboard); final access model = admin tier for everyone, Dev accounts excluded from the CRM only.',
      'Role display + validation for the three-role model across Settings.',
      'Follow-up fixes: migration 076 repointed at the real base table (it targeted the prod `agents` VIEW and would have crashed migrate:prod); Settings sub-nav lag fixed (Users / Roles & Permissions / Linking now switch instantly); permissions matrix shows the three new roles incl. an Access CRM row.',
    ],
  },
  {
    date: '2026-07-29',
    title: 'Pipeline bands, snapshots & the scheduler lands',
    tags: ['CRM'],
    bullets: [
      'PRD-03 Phase 1: three-band pipeline with Trial Active and a monotonic guard (+ seeds).',
      'PRD-03 Phase 2: immutable month-end pipeline snapshots + baseline.',
      'PRD-03 Phase 3: forecast-accuracy view built from the immutable snapshots.',
      'PRD-02 Phase 4: metrics registry + shared <Metric> component + a glossary page.',
      'PRD-02 Phase 5: drill-through record lists + metric reconciliation tests.',
      'Book a call / meeting on any lead (the PRD-04 scheduler).',
    ],
  },
  {
    date: '2026-07-28',
    title: 'Demo card layout',
    tags: ['CRM'],
    bullets: ['Meeting details + booked-by moved above the Source/Added/Trial rows on demo cards.'],
  },
  {
    date: '2026-07-27',
    title: 'Analytics rebuild, pipeline reporting, search everywhere',
    tags: ['CRM'],
    bullets: [
      'Pipeline reporting Phase 1: conversion engine + estimated deal values.',
      'Pipeline reporting Phase 2: per-deal override with an audit trail + weighted report APIs.',
      'Pipeline reporting Phase 3: rebuilt Analytics page + audited deal-override UI.',
      'Analytics fits one screen: pinned header + KPI row, tabbed fixed-height content; forecast as the hero figure; sticky-header bleed fix; menu order.',
      'WeQuote billing MRR ingested (zeros are real data); deal values become actuals.',
      'Supplier flag: suppliers + zero-MRR accounts filtered from all working views; webhook-side is_supplier stamping goes live.',
      'Search bars on Shows/Events (cards, table, in-event contacts), Demo Pipeline, and Billing; prominent consistent styling (icon + brand glow + clear button).',
      'Event/show details are editable: name, type, date, location, notes.',
      'Event views: per-attendee lifecycle stage + filter chips; metric fixes.',
      'CSV/event imports link rows to existing companies instead of dropping them.',
      "Meeting notes Phase 2: Gemini 'Next steps' become tasks and Claude picks the Next task; manually attached Google-Doc transcripts parse like auto-pulled notes; Calendly proxy Meet links resolve; events match by invitee + time.",
    ],
  },
  {
    date: '2026-07-24',
    title: 'Held demos, Calendly hosts & next tasks',
    tags: ['CRM'],
    bullets: [
      'Demos Booked column splits upcoming vs held demos with a HELD divider.',
      "Mark-as-next on deal tasks; kanban cards show the 'Next Task' under a divider.",
      'Calendly demo host captured; the lead auto-assigns to that agent.',
      'Calendly-born deals adopt the WeQuote company name on signup match.',
      'Demo→trial flow: on-trial leads leave Demos; 1-hour held rule.',
      'Gemini meeting notes Phase 1: the notes doc auto-pulls onto each held demo.',
    ],
  },
  {
    date: '2026-07-23',
    title: 'Live inbox + live sync (no more refreshing)',
    tags: ['Support', 'Widget'],
    bullets: [
      'The inbox/queue list refreshes live on every incoming customer message (it only reacted to new chats before — follow-up messages from chats you did not have open never surfaced until refresh).',
      'The socket never permanently gives up reconnecting (was capped at 10–20 attempts, so a long blip / sleep-wake / deploy left the app dead until refresh).',
      'On every reconnect — and whenever the tab regains focus — the open conversation re-fetches to pull in anything missed during the gap. Same on the customer widget.',
    ],
  },
  {
    date: '2026-07-23',
    title: 'Sync integrity & logos',
    tags: ['CRM'],
    bullets: [
      'Calendly-created leads are ACTIVE LEADS, not fake on-trial cards; trial/paid events match by company domain.',
      'Consistency pass: duplicate twins fixed + inverted onboarding semantics corrected; continuous auto-sync.',
      'Tolerant onboarding-event mapping: any onboarding-named event applies as a status.',
      "Sync auto-archives deals whose WeQuote account vanished from the bulk API.",
      'Sync lock: boot-time orphan clear; lock contention shown as info, not failure.',
      'Calendar-day dates; events outrank lagging bulk onboarding status; live board.',
      "Company logos: deal.logoUrl from stored WeQuote meta with logo-aware avatars; sync ingests WeQuote's new logo_url (a logo change counts as a deal change).",
    ],
  },
  {
    date: '2026-07-22',
    title: 'The demo-data purge & board restructure',
    tags: ['CRM'],
    bullets: [
      'All frontend mock fixtures removed (no more staged data) and the fabricated sample-usage generator deleted from the deal Platform tab — every number is real.',
      "Leads split On Trial into 'On Trial' and 'Trial Expired'; leads paginate 50 per page per tab and load the full set.",
      "Board: the two demo columns merge into 'Demos Booked'; Trial Extended removed; Cold Leads dropped; On-Trial split by onboarding; the Automation page removed.",
      'Kanban columns paginate and load fully, with pinned per-column search + pager.',
      "WeQuote's real activation date used (not the import date); activated_at is the source of truth on every sync; honest go-live/sign-up dates; company size from real user_count; ready for trial_started_at / trial_ends_at.",
      'WeQuote data sync becomes a background job with a progress bar, coverage counts, and an all-status rename.',
      'Duplicate deals de-duped — and duplicates made impossible.',
      'Calendly demos: capture, company-domain matching, Google Meet links, board display; Calendly history backfill; a Meetings tab per lead; demo cards stand down after the meeting.',
      'Onboarding status as real data: customer_onboarding_* events + a See Pending filter.',
      'Unified lifecycle categories across Board / Customer Success / Leads + a Customer Success rebuild.',
      'Companies/Contacts: null-safe search (fixes a crash while typing) + 50/page pagination.',
      'Page audit: five silently-empty pages fixed (a rejected limit param) + Demo Pipeline running on real meetings.',
      "Demo cards: first-class 'Booked by' section with an explicit not-the-lead-contact badge.",
      'Demo Pipeline: fixed top (next demo + KPIs + lifecycle) with tabbed scrollable lists.',
      'crm-reset gains a --demo mode (keep only WeQuote-synced customers; purge all demo/fake).',
    ],
  },
  {
    date: '2026-07-21',
    title: 'TO-DO notebook + Dev Board + sync mapping',
    tags: ['Support', 'CRM', 'Platform-wide'],
    bullets: [
      'Personal TO-DO notebook: a private per-agent paper-notebook list docked to the right edge — Today/Tomorrow/Week/Pending tabs, drag-reorder, overnight rollover of unfinished Today tasks into Pending, expanded mode with subtasks + notes. Also available in Dev Mode (same list).',
      'Dev Mode notification suppression (item 1) + Dev Board Phase 0 shell.',
      'Dev Board Phase 1: the Priority List rebuilt in React.',
      "CRM real agents: the CRM's users are the Support agents — one user list.",
      'WeQuote customer sync: status→stage mapping; stop skipping non-live accounts; status gate on CREATE; batch matching (fixes the Preview 504) + a server-side load script; CRM key decoupled from the support key.',
      'Usage auto-enables Setup modules (one-way).',
    ],
  },
  {
    date: '2026-07-17',
    title: 'Off-hours pickup fix + transfers ring',
    tags: ['Support', 'Mobile'],
    bullets: [
      'Off-hours pickup fixed: once an agent picks up a ticket-converted chat, the customer replying no longer closes/locks the chat (both send paths now check for an assigned agent).',
      'Transfers ring: transferring a chat rings the recipient like a new chat on web AND mobile (20s ringtone) with a "Transferred to you" card, until they open it.',
    ],
  },
  {
    date: '2026-07-16',
    title: 'Dev Mode + CRM archive',
    tags: ['Platform-wide', 'CRM'],
    bullets: [
      "Dev Mode MVP: mode switch persisted with a confirmation dialog; /dev blank workspace with its own minimal shell (only a 'Support Mode' button); TopBar 'Dev Mode' button next to AI Copilot; all Support/CRM routes redirect to /dev in dev mode so no nav/notifications/rings mount.",
      'Phase A: archive (soft-delete) for CRM leads, shows, opportunities, contacts, companies.',
      'Phase B+C: the delete (archive) UI across the CRM.',
    ],
  },
  {
    date: '2026-07-15',
    title: 'Emailed screenshots arrive as images',
    tags: ['Support', 'CRM'],
    bullets: [
      "Inbound-email images fixed: a customer's emailed screenshot showed only as filename text because the inbound path never extracted attachments — attachments now save to storage and attach to the thread, with a seen/saved log line.",
      'CRM Phase 2.10: duplicate hints that do not invent duplicates.',
    ],
  },
  {
    date: '2026-07-14',
    title: 'CRM truth pass — Phases 1 & 2',
    tags: ['CRM'],
    bullets: [
      'Phase 1.1: WeQuote native lifecycle event ingest + a webhook probe.',
      "Phase 1.2: platform usage reshaped to WeQuote's real capability.",
      'Phase 1.3: inbound WeQuote usage webhook + per-account presence.',
      'Phase 1.4: the Platform tab shows activity + engagement, not hours.',
      'Phase 1.5: the usage chain seeded — flagged, evictable, verified; rank-based personas; stray test-data cleanup; honest baseline reporting.',
      'Phase 2.1: Support joined to the CRM (chats linked to leads); lead → paid-customer link dry-run tooling.',
      'WeQuote live-customer sync: manual, preview-first, non-destructive.',
      'Phase 2.2: churn scoring rebuilt on evidence (health checks respect terminal deals).',
      "Phase 2.3: module adoption derived from usage, not a checkbox — and no usage feed means adoption is UNKNOWN, not 'unused'.",
      'Phase 2.4: show/event outcomes come from the leads, not opp.status.',
      'Phase 2.5: sequences can finally enrol — and only on purpose; pre-armed sequences disarmed.',
      'Phase 2.6: CRM alerts are delivered, not just recorded.',
      'Phase 2.7: the funnel measures flow, not a snapshot (stuck-count display cap fixed; the goal stage is not a jam).',
      "Phase 2.8: reports can see the new data — and every number states its sample.",
      'Phase 2.9: an owner who is not a real agent is an owner of nobody.',
    ],
  },
  {
    date: '2026-07-13',
    title: 'Message editing, image editor, previews & links',
    tags: ['Support', 'Widget'],
    bullets: [
      'Phase 1 chat nav: a Back button in the chat header returning to the correct list, and the selected tab (e.g. Missed) is retained when opening a chat (mirrored in the URL so remounts cannot snap back to Queued).',
      'Phase 2: image thumbnail previews above the composer on both sides (widget 56px tiles + paste-to-attach; dashboard 64px), each removable, multiple sent together.',
      'Customers can finally click links agents send: the widget rendered bodies as raw text — now linkified into real anchors opening in a new tab, readable on both bubble colours, trailing punctuation handled, attachment-only messages guarded.',
      'Phase 3: WhatsApp-style image editor on both sides — click a thumbnail to draw freehand (6 colours), add text, undo; Done uploads the annotated PNG in place. Plain canvas, no new dependency.',
      'Editor fixes the same day: rendered via a portal (was clipped invisible by a transformed ancestor); oversized-image errors surface in a dismissible banner; the text tool fixed (blur-commit was closing it instantly — Enter/✓ commits, Escape cancels).',
      'Editor re-architected: raster base + text OBJECTS — select a placed label to retype, resize, recolour, box (auto-contrast), drag or delete; no text stroke; the editor only closes on Cancel/Done, not backdrop clicks.',
      'Agents can edit their own sent messages: the customer silently sees the corrected text (no edited marker); every version incl. the original is kept as an internal audit record; hover row gains Edit + View Versions with a vertical version timeline.',
    ],
  },
  {
    date: '2026-07-13',
    title: 'Email templates & CRM navigation',
    tags: ['CRM'],
    bullets: [
      "Email Templates page porting the portal's block + {Tag} model, with a true email preview; sequences can send templated emails.",
      'Per-sequence on/off toggle (same control as Automation); Automation = internal processes vs Sequences = external comms clarified, with cross-links.',
      'Customer Success: churn analysis across EVERY lead.',
      'Demo Pipeline integrated with lead records + pipeline stages; reorganised.',
      'CRM nav & labels + Report Builder fixes.',
    ],
  },
  {
    date: '2026-07-09',
    title: 'Lead merging + the picked-up-chat sync fix',
    tags: ['CRM', 'Support'],
    bullets: [
      'Board gains a Cold Leads column for uploaded (non-trial-API) leads.',
      'Merge leads (cold lead ↔ trial signup): picker highlights exact company-name matches green and similar ones yellow, sorted by normalised name similarity.',
      'Merge picker: stage filter chips (All/Cold/On trial/Live), search by company or contact, 300-candidate cap with a taller list.',
      'Cold→trial auto-merge: when a cold lead signs up for a trial (matched by email) it promotes in place — no duplicate lead.',
      'Board wheel scrolling scoped to columns (no accidental horizontal board scroll on mouse wheel).',
      "Fixed web live-sync for picked-up chats: the room-join auth compared a number to a string with strict equality (always false), denying agents their own chat's live updates — messages only appeared on refresh.",
    ],
  },
  {
    date: '2026-07-08',
    title: 'Lead import wizard (5 phases)',
    tags: ['CRM'],
    bullets: [
      'Phase 1: upload + parse (CSV/Excel).',
      'Phase 2: auto-mapping UI for columns.',
      'Phase 3: validation, misalignment flags & preview.',
      'Phase 4: commit.',
      'Phase 5: skill handoff.',
      'Fix: unified columns bind to first-class lead fields, so imported Contact/Company/Location/source fields display on the lead.',
    ],
  },
  {
    date: '2026-07-07',
    title: 'Chat History, transcripts by email & attachment fixes',
    tags: ['Support'],
    bullets: [
      'Chat History menu: company-grouped transcript archive — sortable company cards (name, total time, total chats, avg resolution) and a company page with every chat as an expandable transcript with inline attachments + Back button.',
      "Chat History totals corrected twice the same day: first from session-connected time to first→last message span, then to ACTIVE talking time (capped 5-minute gaps between consecutive messages); scoped to widget chats only; company logos added.",
      "Analytics: 'Claude AI Suggestions' card replaced with a 'Most Active Chats' table (logo, name, #chats, time, avg resolve) linking into Chat History.",
      'Email Transcript implemented (was a silent no-op): sends the full transcript to the team mailbox AND the visitor; mobile-friendly two-column layout with real names.',
      'Transcripts auto-email on every chat close (agent close or visitor end), feature-flagged, default on.',
      'Attachments persist on the REST send path (images survive reload + appear in transcripts); transcript emails render image attachments inline with working absolute links; message line breaks preserved.',
      'Multi-attach + drag & drop added to the dashboard composer; the widget goes multi-attachment with drag & drop + multi-select.',
      'Typing/preview spillover across chats fixed (leave_chat on switch + chat-scoped handlers).',
      "Chat Analytics panel scoped correctly: by company alias (falling back to the person's email), so one company's panel no longer lists everyone's chats.",
    ],
  },
  {
    date: '2026-07-07',
    title: 'CRM pages batch (6 pages)',
    tags: ['CRM'],
    bullets: [
      'Opportunities: CSV/Excel contact import.',
      'Leads page.',
      'Demo Pipeline built from the Board.',
      'Billing tabs.',
      'Sequences builder/editor + CRM sidebar icons.',
      'Internal Automation + shared Flags.',
    ],
  },
  {
    date: '2026-07-06',
    title: 'Deal modal v2 — all 8 phases in one day',
    tags: ['CRM'],
    bullets: [
      'Phase 1: HubSpot-style three-pane shell — left section nav (Lead Details / Activity / Tasks / Setup / Platform / Quotes / Invoices / Email sync), centre content, right rail; widened to ~1200px, responsive.',
      'Phase 2: Lead Details as clustered cards (Contact / Company / Deal-Commercials / WeQuote Account / Signup Answers); questionnaire answers render as one editable block that round-trips.',
      'Phase 3: Activity + Timeline merged into one de-duped reverse-chronological feed with composer on top and All/Notes/Calls/Emails/System filters.',
      'Phase 4: Tasks = a lazy-seeded 7-step default checklist + custom tasks, drag-to-reorder (persisted), toggles logged into Activity, X/Y progress in the summary line.',
      'Phase 5: Invoices — drag-drop/picker upload, list with filename/date/uploader/size, download, delete.',
      'Phase 6: Setup — per-module setup state (none/in-progress/done) + live toggle, canonical module list, persisted.',
      'Phase 7: Platform usage section — hero total-hours-this-week + delta vs baseline + sparkline; per-module grid with live indicators, trends, and the right baseline label.',
      'Phase 8: weekly usage pull + comparison backend — Monday 07:00 scheduler, cohort averages per size band, correct baseline selection (cohort in first month, else own prior month).',
    ],
  },
  {
    date: '2026-07-06',
    title: 'Ring-timeout & widget-refresh fixes',
    tags: ['Support', 'Widget', 'Mobile'],
    bullets: [
      "Notice 'notifications off' (mute_alerts) now silences MOBILE push too — no push of any kind during a muted notice (silent ring-stop pushes still work).",
      'Ring-timeout no longer closes picked-up chats: the missed-chat sweep requires no assigned agent (candidate queries + a conditional update guard), so an assigned chat can never be auto-missed. Fixes chats flapping closed/open every ~2 minutes.',
      'Widget refresh fixes: reloaded history shows the real agent name (resolved by sender id), and the on-screen session timer survives refresh (anchored to the actual chat start).',
    ],
  },
  {
    date: '2026-07-02',
    title: 'Lifecycle Board & deal-detail restructure',
    tags: ['CRM'],
    bullets: [
      'New Lifecycle Board: a six-column trial→live pipeline (On Trial / Trial Ended / Trial Extended / Demo for On Trials / Demo for Live / All Live) with column-specific card layouts, then bound to live deal data and made the /crm/board landing.',
      'All WeQuote-supplied fields captured: plan + a wequote_meta JSON store merged from webhooks (no incoming data dropped), rendered in a WeQuote Details section on the deal.',
      "Deal-value display fixed: unknown country codes default to £, non-numeric amounts coerce to 0, zero-value deals show '—' (no more 'undefined0').",
      'Deal detail restructured: a Lead Details first tab (contact + onboarding answers), Deal team moved to the top-right, Lead score/Close probability/Stage/Qualification spaced out, AI health to the bottom.',
      'Board scroll: horizontal-only with per-column vertical card lists and pinned headers; vertical mouse wheel scrolls the board sideways (columns still scroll their own cards).',
      "Opportunities: even card spacing, equal-height group cards (date pinned to bottom), cleaner stats, more even conversion-bar spacing, a 'Click to see more' hint; wider section spacing.",
      "Systemic vertical-spacing fix: Tailwind v4's space-y utilities were collapsing across the CRM — restored globally with no markup changes.",
    ],
  },
  {
    date: '2026-07-01',
    title: 'CRM manual entry + the teal-widget fix',
    tags: ['CRM', 'Widget'],
    bullets: [
      'CRM manual data entry: real New/Edit forms for deals, companies, contacts + a product-picker quote form, persisting via the live API; deal owner/team pickers use real support agents; board and lists refetch after save.',
      'CRM webhooks accept the existing shared sync key — WeQuote sends its existing key with nothing new to configure.',
      "The teal-widget regression fixed: CRM routers had mounted a blanket auth on /api which 401'd the public widget-config endpoint, dropping the widget to a hardcoded teal. Auth scoped to /crm; the working agreement + regression log added to CLAUDE.md.",
      'Links inside outgoing blue bubbles render white (not blue) so they are readable; incoming/whisper/WhatsApp links stay blue.',
      "Widget + launcher notice banners unified: the notice's own title + body render in the blue style (blue text on very light blue), replacing the hardcoded amber box; clean render when only a body is set.",
    ],
  },
  // ─── June 2026 ───────────────────────────────────────────────────────────
  {
    date: '2026-06-30',
    title: 'The CRM goes live (frontend + backend)',
    tags: ['CRM'],
    bullets: [
      'Full-fidelity CRM frontend: Kanban Board with a deal-detail modal (activity, tasks, quotes, email, timeline, lead score, BANT, close-probability, team, stage moves with lost-reason), Opportunities (event groups + convert-to-deal), CRM Settings (stage weights/SLA/permissions/webhooks), and rebuilt Companies, Contacts, Demo Tracker, Analytics, Billing, Automation, Sequences, Report Builder, Customer Success, Import.',
      'Phase 3 backend: CRM tables (additive, idempotent) + boot seed; the full agent API — board, deals CRUD + stage moves, activities/tasks/quotes, companies/contacts, opportunities + convert, products, sequences/automations toggles, analytics, demo funnel, settings.',
      'Server-side AI endpoints (env-gated); an automation/sequence engine; inbound webhooks (Calendly HMAC + 6 WeQuote lifecycle events, inert until secrets are set); paid-customer linking on signup.',
      'Deal detail hydrates the full persisted deal on open, so saved notes/tasks/quotes reload correctly.',
      'Widget colour locked to brand red at the API level (config endpoints force it), and the colour picker removed from Widget Settings.',
    ],
  },
  {
    date: '2026-06-26',
    title: 'Paste-to-attach + cross-device ring stop',
    tags: ['Support', 'Platform-wide'],
    bullets: [
      'Paste a screenshot / copied image into the chat composer to attach and send it; message Copy hardened with a fallback.',
      'Chat upload 401 fixed (agent uploads use agent auth, not customer auth) — pasted images and the attach button work.',
      'The web ring stops when a chat is picked up from ANY device: the alert list re-syncs on every queue update + a faster 10s backstop.',
      'New app icon: favicon (ico/svg/16/32), apple-touch-icon, android-chrome 192/512, web manifest.',
    ],
  },
  {
    date: '2026-06-25',
    title: 'Ring rules & the atomic pickup claim',
    tags: ['Support', 'Mobile'],
    bullets: [
      "Mobile push plays the 20-second WeQuote ringtone for NEW chats (env-overridable, Android channel routing); everything else keeps the default ding; 'none' stays silent.",
      'Ring rules: the ringtone is for incoming new chats only; picking up your own chat sends you no push; assigning/transferring to someone else uses the short ding (later upgraded to a full ring on transfer).',
      'When a chat leaves the queue (someone picked it up, or the visitor ended it), a silent data-push stops the ring on every other agent\'s phone.',
      'Pickup is an atomic claim: assign only succeeds if nobody has the chat; the second agent gets a clean 409 "already taken". Admin/supervisor takeover still works.',
      "Whisper invites also push to the invitee's phone (ding + deep-link into whisper); the web invite uses the short ding, not the queue chime.",
    ],
  },
  {
    date: '2026-06-24',
    title: 'Platform reorg: one app with CRM | Support',
    tags: ['Platform-wide'],
    bullets: [
      'Tabbed Dashboard (CRM | Support), a global top bar (Copilot / Sound / Settings / Profile), collapsible Support + CRM sidebar groups, a consolidated Settings hub, and old-route redirects.',
      'CRM section scaffolded from the product mockups (Board, Companies, Contacts, Demo Pipeline, Analytics, Billing, Customer Success, Report Builder, Automation, Sequences, Import) with status colours and sidebar badges.',
      'Mulish becomes the dashboard font; purple-gradient Copilot button; Resolution Notes / Escalations page (chats.escalated).',
      'Visitor conversation-count fix (email OR customer id) + a Conversations stat.',
      'Notices: the message is optional (title only); the widget shows when the team is back online under the unavailable message; the launcher uses the built-in yellow banner with the back-online time.',
      'Ring timeout → ticket tightened to 2 minutes, mirroring the notice-hours conversion exactly (same messages + widget lock) — and it converts even if the visitor never typed (was silently auto-closed).',
      'CRITICAL sweep fix: the missed-chat sweep was querying a stale database VIEW missing newer columns, so it errored every tick and never converted anything — repointed at the base table.',
      'Widget session timer derives from a wall-clock start (recomputed on focus), so it no longer freezes when the widget is backgrounded.',
      'Resolution Notes resolve + show the company name and logo like normal chats.',
      'Visitor drawer: fixed evenly-spaced info with only the conversations list scrolling; sidebar breathing room.',
    ],
  },
  {
    date: '2026-06-23',
    title: 'Bulk actions & the design system',
    tags: ['Support', 'Platform-wide'],
    bullets: [
      'Bulk actions: multi-select with Delete/Assign on Chats (history, missed) and Tickets — new bulk endpoints (chat delete cascades dependents + backing tickets; admin/supervisor only), a reusable selection hook, bulk-action bar, and assign modal.',
      'Ticket agent replies mirror into the chat as plain text — the widget and mobile app no longer show literal HTML tags (the outbound email keeps full HTML).',
      'Canonical <Button> primitive + DESIGN-SYSTEM.md; Tickets inbox converted as the reference.',
      'Customer detail: clear gaps between the accordion sections.',
    ],
  },
  {
    date: '2026-06-22',
    title: '30-day logins, Copilot forwarding & widget resume',
    tags: ['Platform-wide', 'AI', 'Widget'],
    bullets: [
      'Agent login lifetime extended 8h → 30 days (env-overridable) — no more daily logouts on web + phone; one re-login per device to mint the long token.',
      'Copy + Forward on message bubbles: forward a customer message into the AI Copilot (prefilled, focused, never auto-sent) and forward the answer back to the originating chat/ticket via a one-click return target or an open-chat picker; "forwarded from" chip + Back-to-chat.',
      'Copilot accuracy: retrieval widened (top-K 4→12, relevance gate 1.0→0.3, ~100k-char context budget, all env-tunable) + hybrid keyword matching so the right page surfaces even when full-text ranks it low; stops over-clarifying.',
      "Returning widget visitors resume their existing open chat, keyed on the visitor's email scoped to the account (never the shared customer id) — closing the tab, a new tab, or another device rejoins the running chat. Anonymous visitors unaffected.",
      'The widget opens straight into the existing open chat: a non-creating find-open endpoint lets the launcher skip the welcome menu (opening never creates a chat).',
      'Visitor details drawer from the map/list: time spent, conversation history with transcripts, and companies appeared under; both modals portal above the map.',
      'Customer list shows everything (pagination removed, filters intact).',
      'Customer comm-links: ONE auto widget channel link per customer (migration de-dupes the per-chat duplicates).',
      'Forwarding UI: taller reply composer; the Forward-to-Copilot chooser rendered in a flip-to-fit portal, New chat pinned on top, list scrollable without the popup closing.',
    ],
  },
  {
    date: '2026-06-19',
    title: 'Rich ticket replies',
    tags: ['Support'],
    bullets: [
      'Ticket email replies get TipTap formatting (bold/italic/underline/lists/links).',
      'File attachments email as real attachments.',
      'Per-agent signatures, server-appended once.',
      'HTML messages sanitised on both sides and rendered as clean prose.',
    ],
  },
  {
    date: '2026-06-18',
    title: 'Email-first ticketing + AI Copilot launch',
    tags: ['Support', 'AI'],
    bullets: [
      'Email-first ticketing (Zoho-Desk model): whole-inbox Gmail sync — ANY email to the support mailbox becomes a ticket (30-day backfill + live History API; spam/auto-reply/own-mail skipped; attachments stored).',
      'Email-origin tickets join a shared queue with race-safe claiming (a 409 if someone beat you to it); replies email back in-thread; status lifecycle where a customer reply reopens.',
      "Two-pane ticket inbox (Outlook/Chats style): list on the left, the selected ticket's email thread + reply composer on the right; deep-linkable /tickets/:id; responsive.",
      'AI Copilot launches: a grounded, no-hallucination help assistant — versioned skill ingestion with diffing, lexical retrieval with a relevance gate, per-agent streaming chat sessions, admin/supervisor skill updates, a Claude-style /ai-copilot UI.',
      'Copilot context hard-capped per question (current session, last 6 turns, trimmed) to keep token costs flat.',
      'Local-disk fallback for attachment storage when S3 is not configured (path-traversal guarded).',
      "Production .env loads with override:true so key changes apply on restart (fixes 'AI not configured' despite the key being set); retired Claude model replaced with the current one, env-overridable.",
    ],
  },
  {
    date: '2026-06-17',
    title: 'Visitor map + two-way ticket email + the shared-chat fix',
    tags: ['Support', 'Widget'],
    bullets: [
      'Visitor map: server-side IP geolocation (GeoLite2, city-level) of the real client IP; live presence via sockets; react-leaflet map UI; offline history retention.',
      'The map captures + shows visitor name and organisation (identity forwarded from the widget; company name/logo resolved from the account alias; org-logo avatars).',
      'Full roster view: online visitors (green) on top, offline (grey, last known location) below by last-seen; offline pins plotted grey and clustered.',
      'CRITICAL: widget visitors no longer share one chat — the customer-id-keyed resume guard collapsed different people into one conversation where they saw each other\'s messages. Removed; every open gets its own chat.',
      "Chat header + visitor list show the real visitor name (not 'Account WQ-…').",
      'Widget bubble text wraps correctly (three successive fixes: long unbroken strings, embedding-site CSS interference, and short messages collapsing to one character per line).',
      'Two-way ticket email via Resend: tickets send from support@ with a per-ticket reply address; a verified inbound webhook appends customer replies to the chat thread and reopens the ticket (dedup table included).',
      'Gmail-API alternative transport (service-account delegation) with a background poller, feature-flagged.',
      'Notifications layer above the map (z-index fix).',
    ],
  },
  {
    date: '2026-06-16',
    title: 'UI bugs batch + ticket reopen',
    tags: ['Support', 'Widget'],
    bullets: [
      "Reopen a ticket as a chat: clears the conversion latch and emits a reopen event so the visitor's widget unlocks and they can reply again.",
      'Header/sidebar cutoff fixed; History "All" loads; notices spacing; chat-header ticket buttons; Missed page restyle; closed-missed chats stay missed.',
      'Off-hours: an empty chat (visitor never typed) becomes a ticket after 30 seconds during a notice.',
      'One-open-conversation behaviour on the widget side; Customers width + sync banner; per-notice ticket emails removed where redundant + reopen ring.',
    ],
  },
  {
    date: '2026-06-15',
    title: 'Tickets arrive: missed chats become tickets',
    tags: ['Support', 'Widget', 'Mobile'],
    bullets: [
      'Missed-chat detection: a queued widget chat nobody picks up (5-minute sweep) or one arriving during notice hours converts into a support TICKET and emails the visitor + the team; the chats status set widened to allow missed/assigned/archived.',
      'A Tickets dashboard to work the queue; reopen as chat or email.',
      'Per-notice mute_alerts silences the dashboard chime during notice hours.',
      "The notice-hours flow runs on the widget's ACTUAL socket send path (not just REST), so conversion fires reliably on the first visitor message.",
      "Notice-hours UX: no 'connecting to an agent' during a notice, a one-message lock that stays on the conversation, server-driven notice mode, unconditionally no ringing, and post-conversion visitor messages ignored.",
      'Dashboard alert chime fixed: the browser AudioContext unlocks on first gesture with an "enable sound" affordance; a stuck server-mute that silenced normal chats cleared; foreground browser notifications on incoming chats.',
      'Mobile push fires from the socket send path too, with full diagnostic logging; pushes send to all targeted agents (the dashboard-open exclusion dropped — the app is the sole notification consumer).',
    ],
  },
  {
    date: '2026-06-12',
    title: 'The final-fixes batches (1, 2 & 3)',
    tags: ['Support', 'Platform-wide', 'Mobile'],
    bullets: [
      'Chat list shows company logo/identity and the latest message; My Chats gains open/closed tabs; History gains closed-chat tabs; Visitors row layout fixed.',
      'Collapsible left nav + right info panel; Enter-to-send with Shift+Enter newline; caret colour; natural wrapping; closed chats show full details.',
      'Whisper rework: invite-to-whisper with a top banner + tune; Accept opens the chat in whisper mode; observers can load the invited chat; whisper media; moved to the composer; whisper-mode UI cutoff fixed.',
      'Team metrics: averages, resolution time, CSAT, first-response-at; Analytics aggregations; system messages render as centred status lines; shared PageHeader.',
      'Department membership UI/API so the widget can route chats to a team; team agent detail modal → full agent detail page with company logo/flag/visitor context.',
      'Customer sync pulls organisations + users from WeQuote (logo, country, address, account type, job title) into companies + Contacts, with a backfill endpoint; WhatsApp groups auto-tag on sync.',
      'Country flags on chat cards + sidebar; assign-search fixed; auto-link unlinked visitors + restyle.',
      'The 429 refresh storm fixed: queue updates no longer fan out on every message; rate limiting per session (not per IP) and raised.',
      'Toasts moved off the top-right so they stop covering the pickup alert and page headers.',
      'Expo push notifications land: a push-token registry with register/unregister endpoints, offline-aware, settings-aware, auto-pruning dead tokens, wired to chat events.',
      'Sync Customers pull via configurable WeQuote API env; missed-page restyle; the All tab removed (defaults to Queued); assign-to-company crash fixed.',
    ],
  },
  {
    date: '2026-06-11',
    title: 'Assignment visibility',
    tags: ['Support'],
    bullets: [
      'The handling agent shows on the chat list and header, and follows transfers; the queue join resolves agent name/avatar.',
      'Pickups log a system message and broadcast to the queue; the info panel shows assignment history.',
      'Responsive chat header with an icon-only Whisper button.',
    ],
  },
  {
    date: '2026-06-09',
    title: 'Widget restyle + global pickup alerts + agent profiles',
    tags: ['Widget', 'Support'],
    bullets: [
      'Widget restyle P1 (active chat): WeQuote tokens, a single light header with an MM:SS session pill and kebab End menu, coral/neutral bubbles, refined composer.',
      'Widget restyle P2 (launcher): coral button, light header, Mulish, topic cards with icons, coral CTA.',
      'Online-agents presence strip atop the pre-chat screen; widget auto-provisions the paid customer from full widget identity; launcher moved bottom-left.',
      'Global widget pickup alert: a card + ring for every agent until someone Accepts (assigns + opens) — Decline silences it just for you while others keep ringing; enriched queue events; inbox badge fixed.',
      'Queue cards show visitor + company.',
      'Agent profile page: avatar, password change, activity; name + avatar carried in the login token so the dashboard greets the real person after reload.',
      'Notices: recurring weekly schedules with presets, timezone support and week-wrapping windows — separate engines for WhatsApp auto-replies and widget banners.',
      'Media counts fixed; sidebar company resolution strictly from the URL alias.',
    ],
  },
  {
    date: '2026-06-08',
    title: 'Company-linked chats, the info sidebar & portal identity',
    tags: ['Support', 'Widget'],
    bullets: [
      'Chats tie to a company (alias from the embedding URL) and a person (email/name from the embed identity), captured end-to-end.',
      'Info sidebar for widget chats: icon tabs — collapsible Visitor/Conversation info + an Analytics tab (company/user/chat support time, top issues, unresolved chats, sub-tabbed chat lists); fields always render with an Unavailable fallback.',
      'Most Common Issues extracts recurring keywords/phrases from real chat messages (not resolution tags); unresolved = not closed-and-resolved.',
      'Portal identity API: window.WeQuoteChat.identify / reset / show / hide + ready — alias-scoped analytics.',
      'Onboarding sync endpoint upserts companies + users from the platform (key-authed).',
      'WhatsApp: phone-typed group replies sync into the portal as outgoing; group auto-reply notices resolve the active session so they actually fire; participant + mention names resolve from the push-name cache with avatars.',
      "Quota uses the customer's own monthly minutes (not a hardcoded 60).",
      'The Zoho chats archive UI restored (browse, vector search, chat detail).',
      'Customers: WeQuote id + alias persist on CSV import; alias shown; kebab menu no longer clipped.',
      'Cross-origin headers on the launcher + iframe assets so external sites load them.',
    ],
  },
  {
    date: '2026-06-05',
    title: 'The new WhatsApp engine (Baileys)',
    tags: ['Support'],
    bullets: [
      'WhatsApp gateway rebuilt on Baileys — speaks the protocol directly, no Chromium; sessions persist in a self-contained store and survive restarts; same REST + webhook contract, so nothing else changed.',
      'Media both directions: images, PDFs and voice notes inbound; base64 media outbound.',
      'The queue alarm fires only when the linked number is @mentioned or replied-to in a group.',
      'WhatsApp columns on chats (wa ids/channel/session) power the @mention + DM bridge.',
      'vCard contacts render readable; an upload route for the widget; LIDs captured and mentions logged.',
      '@-mention picker in the dashboard composer: autocomplete with profile pictures; the gateway sends real mentions.',
      'Prod customers/messages schema aligned; chat attachments carried through the socket.',
    ],
  },
  {
    date: '2026-06-02',
    title: 'Production plumbing',
    tags: ['Platform-wide'],
    bullets: [
      'Dashboard socket connects same-origin in production (was hardcoded to localhost).',
      'Compiled migrations load correctly under the production build (ESM/CJS mismatch fixed); the migrate image slimmed.',
    ],
  },
  // ─── May 2026 ────────────────────────────────────────────────────────────
  {
    date: '2026-05-02',
    title: 'Consolidation follow-ups',
    tags: ['Platform-wide', 'Support'],
    bullets: [
      'WhatsApp attachment bridging via URL passthrough.',
      'Socket/visitor follow-up fixes from the consolidation audit; migrate:prod recursion fixed.',
    ],
  },
  {
    date: '2026-05-01',
    title: 'The consolidation: one codebase, one schema',
    tags: ['Platform-wide'],
    bullets: [
      'Secrets moved out of committed config into the environment — and rotated.',
      'The authoritative production schema captured as a baseline; every table brought under 23 versioned migrations with a verified clean-database diff (multiple schema bugs found and fixed on the way).',
      'The legacy dev server fully retired: 22 routes, 10 socket events and the background jobs ported into the real API (with auth tightened where it was loose), smoke-tested end-to-end, then deleted.',
      'Repo cruft cleanup: 87 stale build-artefact files removed.',
    ],
  },
  // ─── April 2026 ──────────────────────────────────────────────────────────
  {
    date: '2026-04-24',
    title: 'Chat stability',
    tags: ['Support'],
    bullets: ['Chat 500 errors fixed (schema-mismatch writes stripped).'],
  },
  {
    date: '2026-04-23',
    title: 'Widget production-ready',
    tags: ['Widget', 'Platform-wide'],
    bullets: [
      'The widget SPA deploys for production iframe embedding; inline launcher scripts allowed on the demo page.',
      'Widget socket hardened: token as a query param, polling-first transport, WebSocket allowed by CSP.',
      'Customer login endpoint for the widget; hardcoded localhost URLs removed; the doubled /api/ prefix fixed.',
      'Widget index.html cache disabled so embedded iframes never go stale.',
      'Chat creation survives a missing sessions table and uses the queued status; assignment accepts both id formats; the socket message handler gains session auth, join_chat and typing preview.',
      'Analytics 500 fixed; embed demo URLs updated.',
    ],
  },
  {
    date: '2026-04-22',
    title: 'First production deployment',
    tags: ['Platform-wide', 'Support'],
    bullets: [
      'The platform ships to production behind nginx.',
      'Rate limiter fixed behind the proxy (real client IPs); webhook endpoints freed from agent auth.',
      'WhatsApp: webhook auto-registration, a DM-to-chat bridge, Chromium path fixed for the gateway.',
      'Production table-name mismatches fixed across services; missing endpoints added.',
      'Agent CRUD endpoints for the Settings page; /agents/stats and /agents/online for the Team page.',
      'Widget-demo + chat-launcher routed through the nginx proxy; production deployment docs + server setup script.',
    ],
  },
  {
    date: '2026-04-21',
    title: 'Deployment prep',
    tags: ['Platform-wide', 'Support'],
    bullets: [
      'A very large merge bringing the platform to deployable state.',
      'Environment loads before route imports; schema-validation compatibility fixes.',
      'WhatsApp: the gateway source committed for production; the Sessions tab accessible when configured-but-not-connected; the connection auto-tests on startup so connected state survives restarts.',
    ],
  },
  {
    date: '2026-04-17',
    title: 'Server manager',
    tags: ['Platform-wide'],
    bullets: ['Production Server Manager package (service status + control), matched to the local pipeline topology with a MySQL monitor fix.'],
  },
  {
    date: '2026-04-16',
    title: 'Agent presence in the widget',
    tags: ['Widget'],
    bullets: ['The widget shows agent identity, a typing indicator, and transfer/close events as they happen.'],
  },
  {
    date: '2026-04-15',
    title: 'Widget Settings, continuous ring & live-sync hardening',
    tags: ['Support', 'Widget'],
    bullets: [
      'Widget Settings page: customise colour, greeting, bubbles and avatars from the dashboard (restyled to dashboard conventions; header colour picked up correctly).',
      'Incoming chats ring continuously, globally, until someone picks up.',
      'Chat rooms re-join on socket reconnect (fixed two silent live-sync bugs); the dashboard socket pointed at the right port so real-time actually works.',
      'Agents stay logged in across dev API restarts.',
      'WhatsApp disconnect state surfaces across the dashboard; the flaky "No active session" on Groups refresh fixed; a linked-customer indicator on Groups (moved to the avatar corner).',
      'The API no longer crashes when agents archive chats.',
      'The dashboard UI primitive layer begins; admin guard fixed on widget-config endpoints.',
    ],
  },
  {
    date: '2026-04-14',
    title: 'Security audit + archive',
    tags: ['Platform-wide', 'Support', 'Widget'],
    bullets: [
      'Critical + High fixes applied from the first security audit batch (including a postMessage-source fix in the widget).',
      'Chat archive feature + audit/SOW docs.',
      'Queue audio alerts fire consistently for repeat chats.',
      'The widget no longer sticks open with a ticking timer after the chat closes.',
    ],
  },
  {
    date: '2026-04-12',
    title: 'WhatsApp archive, search & company details',
    tags: ['Support'],
    bullets: [
      'WhatsApp archive upload wizard with preview and a commit workflow.',
      'WhatsApp groups cache + media gallery; message chevron redesign.',
      'Enhanced search; Zoho card UI; logo display improvements.',
      'WeQuote ID/alias fields added; CSS aligned to design tokens.',
      'Expandable users table on the company details page.',
      'UI tweaks: groups list width, show-password toggle; widget-demo port fix.',
    ],
  },
  {
    date: '2026-04-11',
    title: 'WhatsApp connection UX',
    tags: ['Support'],
    bullets: ['WhatsApp modal: QR auto-starts, API-key visibility toggle, UI fix.'],
  },
  {
    date: '2026-04-10',
    title: 'Paid customers module (the first CRM block)',
    tags: ['CRM', 'Support'],
    bullets: ['CRM paid-customers module with multi-channel linking, migrated onto MySQL, plus bug fixes.'],
  },
  {
    date: '2026-04-07',
    title: 'Forwarding UX',
    tags: ['Support'],
    bullets: ['Forward modal improvements: WhatsApp-style dropdown + multi-select delete.'],
  },
  {
    date: '2026-04-05',
    title: 'WhatsApp groups arrive',
    tags: ['Support'],
    bullets: ['WhatsApp group support with media and sender names, via the OpenWA adapter.'],
  },
  {
    date: '2026-04-03',
    title: 'My Chats',
    tags: ['Support'],
    bullets: ["My Chats personal view; 'Inbox' renamed to 'Chats'; audio alert fix."],
  },
  {
    date: '2026-04-02',
    title: 'Day one: live chat foundations',
    tags: ['Support', 'Widget'],
    bullets: [
      'Agent names in the widget; chat transfer fixed; an online-agents strip.',
      'Online agents on the welcome screen; real-time typing preview; socket fixes.',
    ],
  },
]
